import React, { useId } from "react";
import AddButton from "./AddButton";
import useAddButtonLayer from "../hooks/useAddButtonLayer";

/**
 * AddButtonWithIndicator - A button with a glowing visual indicator line showing where elements will be added
 * 
 * Props:
 * - small: boolean - Use smaller button size
 * - onClick: function - Click handler  
 * - orientation: "horizontal" | "vertical" - Direction of the indicator line
 * - position: "center" | "start" | "end" - Position of button along the indicator line
 * - variant: "page" | "component" - Color scheme (green for page-level, blue for component-level)
 * - shift: "left" | "right" | "none" - Subtle horizontal offset to avoid overlapping with other buttons
 * - className: string - Additional CSS classes
 */
const colorSchemes = {
  page: {
    lineColorRgb: "74, 222, 128",
    buttonBg: "bg-green-500 dark:bg-green-400",
    buttonHoverBg: "hover:bg-green-600 dark:hover:bg-green-500",
    buttonText: "text-white dark:text-gray-900",
  },
  component: {
    lineColorRgb: "37, 99, 235",
    buttonBg: "bg-blue-600 dark:bg-blue-500",
    buttonHoverBg: "hover:bg-blue-700 dark:hover:bg-blue-600",
    buttonText: "text-white",
  },
};

const SHIFT_OFFSET = 24;

const AddButtonWithIndicator = ({
  small = false,
  onClick,
  orientation,
  position = "center",
  variant,
  shift = "none",
  tutorialTargetId,
  ariaLabel,
  className = "",
  layerId,
  layerToken = "default",
}) => {
  const fallbackId = useId();
  const resolvedLayerId = layerId || `add-button-${fallbackId}`;

  const {
    config,
    buttonZ,
    indicatorZ,
    isActive,
    activate,
    deactivate,
  } = useAddButtonLayer(resolvedLayerId, {
    layerToken,
    mode: "button",
  });

  const effectiveOrientation = orientation || config.orientation || "horizontal";
  const effectiveVariant = variant || config.variant || "component";
  const colors = colorSchemes[effectiveVariant] || colorSchemes.component;
  const isHorizontal = effectiveOrientation === "horizontal";

  const gradientStyle = isHorizontal
    ? {
        background: `linear-gradient(to right, rgba(${colors.lineColorRgb}, 0) 0%, rgba(${colors.lineColorRgb}, 0.9) 15%, rgba(${colors.lineColorRgb}, 0.95) 50%, rgba(${colors.lineColorRgb}, 0.9) 85%, rgba(${colors.lineColorRgb}, 0) 100%)`,
        boxShadow: `0 0 8px rgba(${colors.lineColorRgb}, 0.8), 0 0 16px rgba(${colors.lineColorRgb}, 0.4)`,
      }
    : {
        background: `linear-gradient(to bottom, rgba(${colors.lineColorRgb}, 0) 0%, rgba(${colors.lineColorRgb}, 0.9) 15%, rgba(${colors.lineColorRgb}, 0.95) 50%, rgba(${colors.lineColorRgb}, 0.9) 85%, rgba(${colors.lineColorRgb}, 0) 100%)`,
        boxShadow: `0 0 8px rgba(${colors.lineColorRgb}, 0.8), 0 0 16px rgba(${colors.lineColorRgb}, 0.4)`,
      };

  const containerClasses = isHorizontal
    ? "relative w-full flex items-center justify-center"
    : "relative h-full flex items-center justify-center";

  const lineClasses = isHorizontal ? "h-1 w-full" : "w-1 h-full";

  const getButtonPositionStyle = () => {
    if (isHorizontal) {
      if (position === "start") {
        return { left: 0, top: "50%", transform: "translateY(-50%)" };
      }
      if (position === "end") {
        return { right: 0, top: "50%", transform: "translateY(-50%)" };
      }
      const horizontalOffset =
        shift === "left" ? -SHIFT_OFFSET : shift === "right" ? SHIFT_OFFSET : 0;
      return {
        left: "50%",
        top: "50%",
        transform: `translate(calc(-50% + ${horizontalOffset}px), -50%)`,
      };
    }

    if (position === "start") {
      return { top: 0, left: "50%", transform: "translateX(-50%)" };
    }
    if (position === "end") {
      return { bottom: 0, left: "50%", transform: "translateX(-50%)" };
    }

    return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  };

  return (
    <div
      className={`${containerClasses} ${className}`.trim()}
      data-add-button-active={isActive ? "true" : "false"}
    >
      <div
        className="absolute peer group"
        style={{
          ...getButtonPositionStyle(),
          zIndex: buttonZ,
        }}
        onMouseEnter={activate}
        onMouseLeave={deactivate}
        onFocus={activate}
        onBlur={deactivate}
        onTouchStart={activate}
        onTouchEnd={deactivate}
        onTouchCancel={deactivate}
      >
        <AddButton
          small={small}
          onClick={onClick}
          zIndex={buttonZ}
          tutorialTargetId={tutorialTargetId}
          aria-label={ariaLabel}
          className={`
            ${colors.buttonBg} ${colors.buttonHoverBg} ${colors.buttonText}
            shadow-lg
            transition-all duration-200
            ring-2 ring-white dark:ring-gray-800
            relative pointer-events-auto
          `}
          style={{
            boxShadow: `0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 0 0 3px rgba(${colors.lineColorRgb}, 0.2)`,
          }}
        />
      </div>
      <div
        className={`${lineClasses} opacity-0 peer-hover:opacity-100 transition-opacity duration-200 relative pointer-events-none`}
        style={{
          ...gradientStyle,
          zIndex: indicatorZ,
        }}
      />
    </div>
  );
};

export default AddButtonWithIndicator;